import { queryClient } from "@api/queryClient";
import { usePrincipalKey, useSessionKey } from "@auth/sessionStore";
import { useIncomingCalls } from "@notifications/use-incoming-calls";
import { useNotificationsSocket } from "@notifications/use-notifications-socket";
import { inboundRingsComputers } from "@phone/phoneSetupClient";
import {
  enableSoftphone,
  startSoftphoneOwnership,
  stopSoftphoneOwnership,
  useSoftphoneStore,
} from "@phone/softphone";
import { useOutboundLines } from "@phone/useOutboundLines";
import { AuthProvider } from "@providers/AuthProvider";
import { SessionCacheReset } from "@providers/SessionCacheReset";
import { UserProvider } from "@providers/UserProvider";
import { QueryClientProvider } from "@tanstack/react-query";
import { type ReactNode, useEffect, useRef } from "react";

const NotificationsBridge = () => {
  useNotificationsSocket();
  useIncomingCalls();
  return null;
};

/**
 * The headset registration lives and dies with the signed-in principal, so the
 * next person never answers the last one's line. Under brian `inbound_ring` or
 * `outbound_computer_default` the owning tab registers once per ownership grant
 * and retries errors on a backoff; an "off" headset otherwise is the person's choice.
 */
const SoftphoneBridge = () => {
  const userUuid = useSessionKey();
  const principalKey = usePrincipalKey();
  const offered = useOutboundLines();
  const automatic =
    offered.status === "ready" &&
    (inboundRingsComputers(offered.inboundRing) ||
      offered.outboundComputerDefault);
  const ownership = useSoftphoneStore((s) => s.ownership);
  const ownerSince = useSoftphoneStore((s) => s.ownerSince);
  const status = useSoftphoneStore((s) => s.status);
  const autoConnectedFor = useRef<number | null | undefined>(undefined);
  const retries = useRef(0);
  useEffect(() => {
    if (!userUuid) return;
    startSoftphoneOwnership(principalKey);
    return () => stopSoftphoneOwnership();
  }, [userUuid, principalKey]);
  useEffect(() => {
    if (!automatic || ownership === "non-owner" || status !== "off") return;
    if (autoConnectedFor.current === ownerSince) return;
    autoConnectedFor.current = ownerSince;
    void enableSoftphone({ persist: false });
  }, [automatic, ownership, ownerSince, status]);
  useEffect(() => {
    if (status === "ready") retries.current = 0;
    if (!automatic || ownership === "non-owner" || status !== "error") return;
    const delay = Math.min(5_000 * 2 ** retries.current, 60_000);
    const timer = window.setTimeout(() => {
      retries.current += 1;
      void enableSoftphone({ persist: false });
    }, delay);
    return () => window.clearTimeout(timer);
  }, [automatic, ownership, status]);
  return null;
};

export const AppProviders = ({ children }: { children: ReactNode }) => (
  <QueryClientProvider client={queryClient}>
    <AuthProvider>
      <UserProvider>
        {/* Must precede `children`: sibling effects run in tree order. */}
        <SessionCacheReset />
        <NotificationsBridge />
        <SoftphoneBridge />
        {children}
      </UserProvider>
    </AuthProvider>
  </QueryClientProvider>
);
